import { EVENT_STATUS } from '@hedepy/common/enums/EVENT'
import type { Event } from '@hedepy/common/schemas/modules/event/event'
import type { Schedule } from '@hedepy/common/schemas/modules/schedule/schedule'
import isEqual from 'lodash/isEqual'
import { pathOr } from 'ramda'
import { createSelector } from 'reselect'

import type { ClientType } from '../../types/ClientStateType'
import createResultCheckingSelector from '../memoize'
import type { ReduxState } from '../reducers'

const clientsFilter = (state: ReduxState): ClientType[] => pathOr([], ['therapist', 'clients'], state)

const eventsFilter = (state: ReduxState): Event[] => pathOr([], ['therapist', 'appointments'], state)

const scheduleFilter = (state: ReduxState): Schedule[] => pathOr([], ['therapist', 'schedule'], state)

export const clientsObjectSelector = createResultCheckingSelector([clientsFilter], (clients) =>
  clients.reduce<{
    [key: string]: ClientType
  }>((acc, client: ClientType) => {
    acc[client.clientId] = client
    return acc
  }, {}),
)

// The calendar refetches the events on every mount; keeping an equal result stops its grid from remounting
export const therapistCalendarAppointmentSelector = createSelector(
  [eventsFilter, scheduleFilter],
  (events, schedules) => [
    ...schedules.map((schedule) => ({
      ...schedule,
      rRule: schedule.rRule ?? undefined,
      exDate: schedule.exDate ?? undefined,
    })), // a schedule and an event can share an id
    ...events.filter(
      (event) =>
        !event.isCollision &&
        (event.status === EVENT_STATUS.RESERVED ||
          event.status === EVENT_STATUS.PRE_BOOKING ||
          event.status === EVENT_STATUS.COMPLETED),
    ),
  ],
  { memoizeOptions: { resultEqualityCheck: isEqual } },
)
